/* eslint-disable no-console */
const SAMPLE_INTERVAL_MS = 10;
const MAX_BUFFER_SIZE = 10_000;
const ROTATE_MS = 10_000;
const TOP_STACKS = 3;
const MAX_STACK_DEPTH = 25;

interface ProfilerFrame {
    readonly name: string;
    readonly resourceId?: number;
    readonly line?: number;
    readonly column?: number;
}

interface ProfilerStack {
    readonly parentId?: number;
    readonly frameId: number;
}

interface ProfilerSample {
    readonly timestamp: number;
    readonly stackId?: number;
}

interface ProfilerTrace {
    readonly resources: readonly string[];
    readonly frames: readonly ProfilerFrame[];
    readonly stacks: readonly ProfilerStack[];
    readonly samples: readonly ProfilerSample[];
}

interface JsProfiler {
    stop(): Promise<ProfilerTrace>;
}

type JsProfilerConstructor = new (options: { sampleInterval: number; maxBufferSize: number }) => JsProfiler;

export interface SlowFrameScript {
    readonly name: string;
    readonly source: string;
    readonly invoker: string;
    readonly duration: number;
    readonly forcedLayout: number;
}

export interface SlowFrame {
    readonly start: number;
    readonly duration: number;
    readonly scripts: readonly SlowFrameScript[];
}

export class PerfStackSampler {
    private profiler: JsProfiler | null = null;
    private pending: SlowFrame[] = [];
    private readonly rotateTimer: ReturnType<typeof setInterval> | null = null;
    private stopped = false;

    constructor() {
        const ctor = (window as unknown as { Profiler?: JsProfilerConstructor }).Profiler;

        if (!ctor) {
            return;
        }

        try {
            this.profiler = new ctor({ sampleInterval: SAMPLE_INTERVAL_MS, maxBufferSize: MAX_BUFFER_SIZE });
            this.rotateTimer = setInterval(() => void this.rotate(), ROTATE_MS);
        } catch {
            this.profiler = null;
        }
    }

    report(frame: SlowFrame): void {
        if (!this.profiler) {
            logFrame(frame, []);

            return;
        }

        this.pending.push(frame);
        void this.rotate();
    }

    stop(): void {
        this.stopped = true;

        if (this.rotateTimer !== null) {
            clearInterval(this.rotateTimer);
        }

        void this.profiler?.stop();
        this.profiler = null;
    }

    private async rotate(): Promise<void> {
        const { profiler, pending: frames } = this;

        if (!profiler) {
            return;
        }

        this.pending = [];
        this.profiler = null;

        let trace: ProfilerTrace | null = null;

        try {
            trace = await profiler.stop();
        } catch {
            trace = null;
        }

        if (!this.stopped) {
            const ctor = (window as unknown as { Profiler: JsProfilerConstructor }).Profiler;

            this.profiler = new ctor({ sampleInterval: SAMPLE_INTERVAL_MS, maxBufferSize: MAX_BUFFER_SIZE });
        }

        for (const frame of frames) {
            logFrame(frame, trace ? heaviestStacks(trace, frame) : []);
        }
    }
}

interface WeightedStack {
    readonly ms: number;
    readonly lines: readonly string[];
}

function heaviestStacks(trace: ProfilerTrace, frame: SlowFrame): WeightedStack[] {
    const end = frame.start + frame.duration;
    const counts = new Map<number, number>();

    for (const sample of trace.samples) {
        if (sample.stackId !== undefined && sample.timestamp >= frame.start && sample.timestamp <= end) {
            counts.set(sample.stackId, (counts.get(sample.stackId) ?? 0) + 1);
        }
    }

    return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, TOP_STACKS)
        .map(([stackId, count]) => ({ ms: count * SAMPLE_INTERVAL_MS, lines: resolveStack(trace, stackId) }));
}

function resolveStack(trace: ProfilerTrace, stackId: number): string[] {
    const lines: string[] = [];
    let current: number | undefined = stackId;

    while (current !== undefined && lines.length < MAX_STACK_DEPTH) {
        const stack: ProfilerStack = trace.stacks[current];
        const frame = trace.frames[stack.frameId];
        const resource = frame.resourceId !== undefined ? trace.resources[frame.resourceId] : '';
        const location = resource ? ` ${resource}:${frame.line ?? 0}:${frame.column ?? 0}` : '';

        lines.push(`${frame.name || '(anonymous)'}${location}`);
        current = stack.parentId;
    }

    return lines;
}

function logFrame(frame: SlowFrame, stacks: readonly WeightedStack[]): void {
    console.groupCollapsed(`[perf] slow frame ${frame.duration.toFixed(0)}ms at ${(frame.start / 1000).toFixed(1)}s`);

    if (stacks.length > 0) {
        for (const stack of stacks) {
            console.log(`~${stack.ms}ms sampled\n  ${stack.lines.join('\n  ')}`);
        }
    } else if (frame.scripts.length > 0) {
        console.table(frame.scripts.map(script => ({
            ms: Math.round(script.duration),
            forcedLayoutMs: Math.round(script.forcedLayout),
            name: script.name,
            invoker: script.invoker,
            source: script.source,
        })));
    } else {
        console.log('No attribution available.');
    }

    console.groupEnd();
}
